import React from 'react';
import { Shield, Clock, ThumbsUp, Users } from 'lucide-react';

export const About = () => {
  const features = [
    {
      icon: Shield,
      title: 'Qualität & Sicherheit',
      description: 'Höchste Standards bei allen Arbeiten unter Einhaltung aller Sicherheits- und Umweltvorschriften.'
    },
    {
      icon: Clock,
      title: 'Pünktlich & Zuverlässig',
      description: 'Termintreue Ausführung und transparente Kommunikation während des gesamten Projekts.'
    },
    {
      icon: ThumbsUp,
      title: 'Faire Preise',
      description: 'Transparente Angebote ohne versteckte Kosten. Sie wissen von Anfang an, was Sie erwartet.'
    },
    {
      icon: Users,
      title: 'Erfahrenes Team',
      description: 'Qualifizierte Fachkräfte mit langjähriger Erfahrung in allen Bereichen des Bauhandwerks.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-yellow-500 font-semibold text-sm uppercase tracking-wider mb-3">
            Über uns
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Ihr Partner für professionelle Bauarbeiten
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Seit Jahren sind wir in Bochum und Umgebung Ihr zuverlässiger Ansprechpartner für alle Dach- und Abbrucharbeiten.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative flex justify-center items-center py-8 px-4">
            {/* Yellow/Red LED Glow Effect Behind Logo */}
            <div className="absolute inset-0 flex justify-center items-center">
              <div className="w-64 h-64 md:w-96 md:h-96 bg-gradient-to-r from-yellow-500 to-red-500 rounded-full blur-3xl opacity-30 animate-pulse"></div>
            </div>
            
            {/* Logo with Rounded Corners */}
            <div className="relative z-10 w-full max-w-sm md:max-w-md">
              <img
                src="https://customer-assets.emergentagent.com/job_645cca0b-6439-43de-b5cd-72299d912cea/artifacts/bp1dhs8c_Gemini_Generated_Image_cef3smcef3smcef3.png"
                alt="Balazs Dach & Abbruch Service Logo"
                className="w-full h-auto object-contain rounded-3xl hover:scale-105 transition-all duration-500"
                style={{
                  filter: 'drop-shadow(0 0 60px rgba(234, 179, 8, 0.5)) drop-shadow(0 0 30px rgba(220, 38, 38, 0.3))'
                }}
              />
            </div>
            
            {/* Floating Card - Over Logo - Responsive positioning */}
            <div className="absolute -top-4 -right-2 md:-top-6 md:-right-6 z-20 bg-gradient-to-br from-red-600 to-red-700 text-white px-4 py-3 md:px-8 md:py-6 rounded-2xl shadow-2xl hover:shadow-red-500/50 transition-all duration-300 hover:scale-105">
              <div className="text-3xl md:text-5xl font-bold mb-1">15+</div>
              <div className="text-xs md:text-sm font-semibold tracking-wide uppercase">Jahre Erfahrung</div>
            </div>
          </div>

          {/* Text Content */}
          <div>
            <h3 className="text-3xl font-bold text-white mb-6">
              Balazs Dach & Abbruch Service UG
            </h3>
            <p className="text-gray-300 text-lg mb-6 leading-relaxed">
              Wir sind ein professionelles Handwerksunternehmen mit Sitz in Bochum und bieten Ihnen das komplette
              Leistungsspektrum rund um Dach, Fassade, Zimmerei und Abbruch.
            </p>
            <p className="text-gray-300 text-lg mb-6 leading-relaxed">
              Unser erfahrenes Team steht für höchste Qualität, Zuverlässigkeit und faire Preise. Egal ob
              Neueindeckung, Sanierung oder kompletter Abbruch – wir sind Ihr kompetenter Partner.
            </p>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Von der ersten Beratung bis zur finalen Abnahme begleiten wir Sie professionell und transparent durch
              Ihr Projekt.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="text-3xl font-bold text-yellow-500 mb-1">500+</div>
                <div className="text-gray-400">Zufriedene Kunden</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-500 mb-1">1000+</div>
                <div className="text-gray-400">Abgeschlossene Projekte</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-500 mb-1">100%</div>
                <div className="text-gray-400">Kundenzufriedenheit</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-yellow-500/20"
              >
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-yellow-500" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">{feature.title}</h4>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};